import { Injectable, inject } from "@angular/core";
import {
  Firestore, collection, collectionData, doc, docData,
  query, where, orderBy, limit, limitToLast,
} from "@angular/fire/firestore";
import { Observable, map, combineLatest } from "rxjs";

// ================================================================
// BEING TELEMETRY SERVICE
// ================================================================
//
// Reads the flat snapshot docs written by functions/src/telemetry.ts.
//
// Two access patterns, deliberately separate:
//   - Population charts read population-telemetry (1 doc per tick).
//   - Per-being charts read beings/{id}/telemetry (1 doc per being/tick).
//
// Never fan out across every being's subcollection to build a population
// view — that's 100 queries per render.
// ================================================================

export interface BeingSnapshot {
  ts: number;
  beingId: string;
  beingName: string;

  alive: boolean;
  causeOfDeath: string | null;
  generation: number;
  ageTicks: number;

  hunger: number;
  energy: number;
  socialNeed: number;
  curiosity: number;
  pleasure: number;
  fear: number;
  resting: boolean;

  genomeCuriosity: number;
  genomeRestlessness: number;
  genomeMetabolism: number;

  lat: number;
  lng: number;
  region: string;
  geohash8: string;
  heading: number;

  cellObservationCount: number;
  cellAveragePredictionError: number;
  notionCount: number;
  visitedCount: number;
  mode: "geo" | "data";
  taskId: string | null;

  thoughts: { kind: string; text: string }[];
  thoughtText: string | null;      // the last one, for a single-line label
  thoughtKind: string | null;
}



export interface PopulationSnapshot {
  ts: number;
  alive: number;
  dead: number;
  deathsThisTick: number;
  byCause: Record<string, number>;
  meanHunger: number;
  meanEnergy: number;
  meanPleasure: number;
  meanFear: number;
  meanSocialNeed: number;
  restingCount: number;
  starvingCount: number;
  exhaustedCount: number;
  criticalCount: number;
  meanNotions: number;
  geoMode: number;
  dataMode: number;
}

/** Chart-ready shape: one named series of {x, y} points. */
export interface Series {
  name: string;
  points: { x: number; y: number }[];
}

export const DRIVE_KEYS = [
  "hunger", "energy", "pleasure", "fear", "curiosity", "socialNeed",
] as const;
export type DriveKey = typeof DRIVE_KEYS[number];

export const DRIVE_COLOURS: Record<DriveKey, string> = {
  hunger: "#e8833a",
  energy: "#3a9ae8",
  pleasure: "#4caf72",
  fear: "#b04ad4",
  curiosity: "#e8c53a",
  socialNeed: "#8a8f98",
};

@Injectable({ providedIn: "root" })



export class BeingTelemetryService {



  private fs = inject(
    Firestore
  );

  // ── Live being document ──────────────────────────────────────
  // Current state, updates in realtime. Use for gauges and the
  // alive/dead badge, not for history.

  being$(beingId: string): Observable<any> {
    return docData(doc(this.fs, `beings/${beingId}`), { idField: "id" });
  }

  alive$(beingId: string): Observable<boolean> {
    return this.being$(beingId).pipe(
      map(b => b?.body?.alive !== false),
    );
  }

  /** All living beings, for a roster or map overlay. */
  livingBeings$(max = 100): Observable<any[]> {
    const q = query(
      collection(this.fs, "beings"),
      where("body.alive", "==", true),
      limit(max),
    );
    return collectionData(q, { idField: "id" });
  }

  // ── Per-being history ────────────────────────────────────────

  /**
   * Last N snapshots for one being, oldest first.
   *
   * limitToLast with orderBy(asc) gives the most recent N already in
   * chart order — cheaper than fetching desc and reversing client-side.
   */
  history$(beingId: string, points = 288): Observable<BeingSnapshot[]> {
    const q = query(
      collection(this.fs, `beings/${beingId}/telemetry`),
      orderBy("ts", "asc"),
      limitToLast(points),
    );
    return collectionData(q) as Observable<BeingSnapshot[]>;
  }

  /** Thoughts positioned on the same ts axis as the drive traces. */
  thoughtMarkers$(beingId: string, points = 288): Observable<{ ts: number; kind: string; text: string }[]> {
    return this.history$(beingId, points).pipe(
      map(snaps => snaps
        .filter(s => !!s.thoughtText)
        .map(s => ({ ts: s.ts, kind: s.thoughtKind!, text: s.thoughtText! }))),
    );
  }

  /** Snapshots since a wall-clock cutoff. `hours = 24` for a day view. */
  historySince$(beingId: string, hours: number): Observable<BeingSnapshot[]> {
    const cutoff = Date.now() - hours * 3600_000;
    const q = query(
      collection(this.fs, `beings/${beingId}/telemetry`),
      where("ts", ">=", cutoff),
      orderBy("ts", "asc"),
    );
    return collectionData(q) as Observable<BeingSnapshot[]>;
  }

  /**
   * Drive history as chart series. Pass the drives you want plotted —
   * all six on one axis is unreadable; hunger + energy is the pair that
   * actually predicts death.
   */
  driveSeries$(
    beingId: string,
    drives: DriveKey[] = ["hunger", "energy"],
    points = 288,
  ): Observable<Series[]> {
    return this.history$(beingId, points).pipe(
      map(snaps => drives.map(key => ({
        name: key,
        points: snaps.map(s => ({ x: s.ts, y: s[key] as number })),
      }))),
    );
  }

  /**
   * Every numeric property in one call, for a small-multiples grid.
   * Returns one series per drive plus behavioural counters.
   */
  allSeries$(beingId: string, points = 288): Observable<Series[]> {
    return this.history$(beingId, points).pipe(
      map(snaps => {
        const series = (name: string, pick: (s: BeingSnapshot) => number): Series => ({
          name,
          points: snaps.map(s => ({ x: s.ts, y: pick(s) })),
        });
        return [
          ...DRIVE_KEYS.map(k => series(k, s => s[k] as number)),
          series("notions", s => s.notionCount),
          series("visited", s => s.visitedCount),
          series("cellError", s => s.cellAveragePredictionError),
          series("resting", s => (s.resting ? 1 : 0)),
        ];
      }),
    );
  }

  /**
   * Death markers for annotating a chart: the ts where alive flips false.
   * Returns [] for a being still living.
   */
  deathMarker$(beingId: string): Observable<{ ts: number; cause: string } | null> {
    return this.history$(beingId).pipe(
      map(snaps => {
        const died = snaps.find(s => !s.alive);
        return died ? { ts: died.ts, cause: died.causeOfDeath ?? "unknown" } : null;
      }),
    );
  }

  // ── Population ───────────────────────────────────────────────

  population$(points = 288): Observable<PopulationSnapshot[]> {
    const q = query(
      collection(this.fs, "population-telemetry"),
      orderBy("ts", "asc"),
      limitToLast(points),
    );
    return collectionData(q) as Observable<PopulationSnapshot[]>;
  }

  /** Alive count over time — the headline survival curve. */
  survivalCurve$(points = 288): Observable<Series[]> {
    return this.population$(points).pipe(
      map(snaps => [
        { name: "alive", points: snaps.map(s => ({ x: s.ts, y: s.alive })) },
        { name: "critical", points: snaps.map(s => ({ x: s.ts, y: s.criticalCount })) },
        { name: "resting", points: snaps.map(s => ({ x: s.ts, y: s.restingCount })) },
      ]),
    );
  }

  /** Mean drives across the population — shows whether tuning worked. */
  populationDrives$(points = 288): Observable<Series[]> {
    return this.population$(points).pipe(
      map(snaps => [
        { name: "mean energy", points: snaps.map(s => ({ x: s.ts, y: s.meanEnergy })) },
        { name: "mean hunger", points: snaps.map(s => ({ x: s.ts, y: s.meanHunger })) },
        { name: "mean pleasure", points: snaps.map(s => ({ x: s.ts, y: s.meanPleasure })) },
      ]),
    );
  }

  /** Deaths per tick, split by cause — bar chart. */
  deathsByCause$(points = 288): Observable<Series[]> {
    return this.population$(points).pipe(
      map(snaps => {
        const causes = new Set<string>();
        snaps.forEach(s => Object.keys(s.byCause ?? {}).forEach(c => causes.add(c)));
        return [...causes].map(cause => ({
          name: cause,
          points: snaps.map(s => ({ x: s.ts, y: s.byCause?.[cause] ?? 0 })),
        }));
      }),
    );
  }

  // ── Combined view for a being detail page ────────────────────

  beingDashboard$(beingId: string, points = 288) {
    return combineLatest({
      being: this.being$(beingId),
      series: this.allSeries$(beingId, points),
      death: this.deathMarker$(beingId),
    });
  }
}